import { useEffect, useRef, useState, useCallback } from 'react';

interface TurnstileProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  className?: string;
}

declare global {
  interface Window {
    turnstile: {
      render: (element: string | HTMLElement, options: any) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      getResponse: (widgetId: string) => string;
    };
  }
}

export function Turnstile({
  siteKey,
  onVerify,
  onError,
  onExpire,
  theme = 'dark',
  size = 'normal',
  className = '',
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // 使用useRef保存最新的回调函数，避免useEffect重新执行
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);
  
  // 更新ref中的回调函数
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
    onExpireRef.current = onExpire;
  });

  useEffect(() => {
    // 检查Turnstile脚本是否已加载
    if (window.turnstile) {
      setIsLoaded(true);
      setIsLoading(false);
      return;
    }

    // 动态加载Turnstile脚本
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      setIsLoaded(true);
      setIsLoading(false);
    };
    
    script.onerror = () => {
      setIsLoading(false);
      onError?.('Failed to load Turnstile script');
    };

    document.head.appendChild(script);

    return () => {
      // 清理脚本
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [onError]);

  useEffect(() => {
    if (!isLoaded || !containerRef.current || !siteKey) return;

    // 渲染Turnstile widget
    try {
      const widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          onVerifyRef.current(token);
        },
        'error-callback': (error: string) => {
          onErrorRef.current?.(error);
        },
        'expired-callback': () => {
          onExpireRef.current?.();
        },
        theme,
        size,
      });

      widgetIdRef.current = widgetId;
    } catch (error) {
      console.error('Turnstile render error:', error);
      onErrorRef.current?.('Failed to render Turnstile widget');
    }

    // 清理函数
    return () => {
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (error) {
          console.error('Turnstile cleanup error:', error);
        }
        widgetIdRef.current = null;
      }
    };
  }, [isLoaded, siteKey, theme, size]);

  // 重置widget
  const reset = () => {
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (error) {
        console.error('Turnstile reset error:', error);
      }
    }
  };

  // 获取响应token
  const getResponse = () => {
    if (widgetIdRef.current && window.turnstile) {
      try {
        return window.turnstile.getResponse(widgetIdRef.current);
      } catch (error) {
        console.error('Turnstile getResponse error:', error);
        return '';
      }
    }
    return '';
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <div className="flex items-center gap-2 text-slate-400">
          <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Loading security check...</span>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <div className="text-center text-slate-400">
          <div className="text-sm">Unable to load security verification</div>
          <div className="text-xs mt-1">Please check your internet connection</div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div ref={containerRef} />
    </div>
  );
}

// 导出hook用于外部访问widget方法
export function useTurnstile() {
  const reset = () => {
    // 这个函数需要从组件实例中调用，这里只是占位符
    console.warn('useTurnstile reset should be called from Turnstile component instance');
  };

  const getResponse = () => {
    console.warn('useTurnstile getResponse should be called from Turnstile component instance');
    return '';
  };

  return { reset, getResponse };
} 